import { z } from 'zod';
import { MyUser } from 'libs/end-to-end/src/my-user';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { FeatureFlags } from 'libs/end-to-end/src/feature-flag';
import { FilterDefinitionForExtrasArray } from 'libs/end-to-end/src/types/databases/filters/filter-definition';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';
import { ViewModeChartZod } from 'libs/end-to-end/src/view-configurations';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';

export const DashboardChartRecordDto = z.object({
  id: z.string().uuid(),
  dashboardId: z.string().uuid(),
  customVizRequest: CustomVizRequest,
  name: z.string(),
  description: z.string().nullable(),
  positionOnDashboard: z
    .number()
    .nullable()
    .transform((val) => val ?? 0)
    .default(0),
  positionOnRow: z
    .number()
    .nullable()
    .transform((val) => val ?? 1)
    .default(1),
  width: z
    .number()
    .nullable()
    .transform((val) => val ?? 100)
    .default(100),
  height: z.number().nullable(),
  defaultViewMode: ViewModeChartZod.default('table'),
  selectedMetric: z.string().nullable(),
  baseColors: z.array(z.string()).nullable(),
  viewConfiguration: ViewConfigurationSchema.default({}),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type DashboardChartRecordDto = z.infer<typeof DashboardChartRecordDto>;

export const CreateOrUpdateDashboardChartDto = DashboardChartRecordDto.extend({
  id: z.string().uuid().optional(),
}).omit({
  createdAt: true,
  updatedAt: true,
});

export type CreateOrUpdateDashboardChartDto = z.infer<
  typeof CreateOrUpdateDashboardChartDto
>;

export const DashboardRecordDto = z.object({
  id: z.string(),
  name: z.string(),
  ownerUserId: z.string(),
  ownerEmail: z.string().nullish(),
  organizationId: z.string(),
  databaseId: z.string(),
  databaseRevisionId: z.string().nullable(),
  isPublic: z.boolean(),
  publicTimestampStart: z.coerce.date().nullable(),
  globalFilters: FilterDefinitionForExtrasArray.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  charts: z.array(DashboardChartRecordDto),
});

export type DashboardRecordDto = z.infer<typeof DashboardRecordDto>;

export const DashboardExternalUserState = z.enum(['active', 'revoked']);

export type DashboardExternalUserState = z.infer<
  typeof DashboardExternalUserState
>;

export const DashboardExternalUserDto = z.object({
  id: z.string().uuid(),
  dashboardId: z.string().uuid(),
  email: z.string().trim().email(),
  state: DashboardExternalUserState,
  invitedByUserId: z.string().uuid(),
  lastInvitationSentAt: z.coerce.date().nullable(),
  revokedAt: z.coerce.date().nullable(),
  revokedByUserId: z.string().uuid().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type DashboardExternalUserDto = z.infer<typeof DashboardExternalUserDto>;

export const DashboardExternalUsersDto = DashboardExternalUserDto.array();

export type DashboardExternalUsersDto = z.infer<
  typeof DashboardExternalUsersDto
>;

export const dashboardExternalInviteMessageMaxLength = 5000;

const InvitationMessageSchema = z
  .string()
  .trim()
  .max(dashboardExternalInviteMessageMaxLength)
  .nullish()
  .transform((value) => value || null);

export const InviteDashboardExternalUsersRequestDto = z.object({
  emails: z.array(z.string().trim().email()).min(1),
  message: InvitationMessageSchema,
});

export type InviteDashboardExternalUsersRequestDto = z.infer<
  typeof InviteDashboardExternalUsersRequestDto
>;

export const InviteDashboardExternalUsersResponseDto = z.object({
  invitedUsers: DashboardExternalUsersDto,
  failedInvitationEmails: z.array(z.string().trim().email()),
});

export type InviteDashboardExternalUsersResponseDto = z.infer<
  typeof InviteDashboardExternalUsersResponseDto
>;

export const ResendDashboardExternalUserInvitationRequestDto = z.object({
  message: InvitationMessageSchema,
});

export type ResendDashboardExternalUserInvitationRequestDto = z.infer<
  typeof ResendDashboardExternalUserInvitationRequestDto
>;

export const UpdateDashboardDto = z.object({
  name: z.string(),
  isPublic: z.boolean(),
  globalFilters: FilterDefinitionForExtrasArray.nullable(),
  charts: z.array(CreateOrUpdateDashboardChartDto),
  databaseRevisionId: z.string().nullable(),
});

export type UpdateDashboardDto = z.infer<typeof UpdateDashboardDto>;

export const DashboardWithChartsAndGuestInfo = DashboardRecordDto.extend({
  guestInfo: z.object({
    myUser: MyUser,
    myObservation: MyObservation,
    organizationId: z.string(),
    databaseId: z.string(),
    databaseRevisionId: z.string(),
    featureFlags: FeatureFlags,
  }),
});

export type DashboardWithChartsAndGuestInfo = z.infer<
  typeof DashboardWithChartsAndGuestInfo
>;

export const MoveDashboardChartCommand = z.object({
  sourceDashboardId: z.string().uuid(),
  sourceDashboardChartId: z.string().uuid(),
  targetDashboardId: z.string().uuid(),
});

export type MoveDashboardChartCommand = z.infer<
  typeof MoveDashboardChartCommand
>;

export const AddChartToDashboardCommand = z.object({
  chart: CreateOrUpdateDashboardChartDto,
  targetDashboardId: z.string().uuid(),
});

export type AddChartToDashboardCommand = z.infer<
  typeof AddChartToDashboardCommand
>;

export const MoveDashboardChartRequestSchema = z.object({
  targetDashboardId: z.string().uuid(),
});

export type MoveDashboardChartRequestSchema = z.infer<
  typeof MoveDashboardChartRequestSchema
>;

export const MoveDashboardChartResponseDto = z.object({
  sourceDashboard: DashboardRecordDto,
  targetDashboard: DashboardRecordDto,
});

export type MoveDashboardChartResponseDto = z.infer<
  typeof MoveDashboardChartResponseDto
>;

export const DraftDashboardChart = CreateOrUpdateDashboardChartDto.extend({
  draftId: z.string(),
});

export type DraftDashboardChart = z.infer<typeof DraftDashboardChart>;

export const DraftDashboard = UpdateDashboardDto.extend({
  charts: z.array(DraftDashboardChart),
});

export type DraftDashboard = z.infer<typeof DraftDashboard>;

export const getNextChartPosition = (
  charts: { positionOnDashboard: number }[],
) => {
  const normalizedCharts = charts ?? [];

  if (normalizedCharts.length === 0) {
    return {
      positionOnDashboard: 0,
      positionOnRow: 0,
    };
  }

  const maxPositionOnDashboard = Math.max(
    ...normalizedCharts.map((c) => c.positionOnDashboard),
  );

  return {
    positionOnDashboard: maxPositionOnDashboard + 1,
    positionOnRow: 0,
  };
};

export const isDashboardChartStreamlitBacked = ({
  customVizRequest,
  viewConfiguration,
}: Pick<DashboardChartRecordDto, 'customVizRequest' | 'viewConfiguration'>) => {
  return (
    customVizRequest.customVizId === 'externalStreamlit' ||
    typeof viewConfiguration.streamlitPrototypeAppId === 'string'
  );
};
